import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Clock, Package, ArrowRight, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RouteMap from "@/components/tracking/RouteMap";
import { toast } from "sonner";

function statusTone(status = "") {
  const s = status.toLowerCase();
  if (s.includes("deliver")) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";
  if (s.includes("customs")) return "text-violet-300 bg-violet-500/10 border-violet-500/40";
  if (s.includes("transit") || s.includes("dispatch")) return "text-amber-300 bg-amber-500/10 border-amber-500/40";
  if (s.includes("vault")) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";
  if (s.includes("registered")) return "text-sky-300 bg-sky-500/10 border-sky-500/40";
  return "text-slate-200 bg-slate-500/10 border-slate-500/40";
}

function fmt(iso, locale) {
  try {
    return new Date(iso).toLocaleString(locale || "en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function TrackPage() {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState(trackingId || "");
  const [loading, setLoading] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doTrack = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setNotFound(false); setPkg(null);
    try {
      const { data } = await api.get(`/packages/track/${encodeURIComponent(id.toUpperCase())}`);
      setPkg(data);
      toast.success(t("track.located", { id: data.tracking_id }));
    } catch (e) {
      if (e?.response?.status === 404) { setNotFound(true); toast.error(t("track.not_found_toast")); }
      else { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { if (trackingId) { setQ(trackingId); doTrack(trackingId); } }, [trackingId, doTrack]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/track/${encodeURIComponent(q.trim().toUpperCase())}`);
  };

  return (
    <>
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-3">
          {t("track.kicker")}
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight">
          {t("track.title")}
        </h1>
        <p className="mt-4 text-slate-300 max-w-2xl">{t("track.subtitle")}</p>

        <form onSubmit={submit} className="mt-8 bg-[#0F1C3F]/70 border border-amber-500/30 rounded-2xl p-3 flex flex-col sm:flex-row gap-3" data-testid="track-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400/70" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("track.placeholder")} data-testid="track-input" className="bg-slate-950/80 border-amber-500/30 text-slate-100 pl-9 font-mono h-12" />
          </div>
          <Button type="submit" disabled={loading} data-testid="track-submit-button" className="h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold px-6">
            {loading ? t("track.searching") : t("track.search")} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </form>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        {notFound && (
          <div className="bg-red-950/40 border border-red-500/40 text-red-200 rounded-xl p-5 flex items-start gap-3" data-testid="track-not-found">
            <ShieldAlert className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-semibold">{t("track.not_found_t")}</div>
              <div className="text-sm text-red-300/80">{t("track.not_found_b")}</div>
            </div>
          </div>
        )}

        {pkg && (
          <div data-testid="track-result" className="dvl-rise">
            <div className="bg-[#0F1C3F]/80 border border-amber-500/25 rounded-2xl p-8 mb-8 shadow-xl shadow-black/40">
              <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2">
                    {t("track.id_label")}
                  </div>
                  <div className="font-mono text-2xl font-bold text-amber-300 bg-amber-950/40 border border-amber-500/30 px-3 py-1.5 rounded inline-block">
                    {pkg.tracking_id}
                  </div>
                </div>
                <div data-testid="track-current-status" className={`border font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${statusTone(pkg.current_status)}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {pkg.current_status}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  [t("track.origin"), pkg.origin],
                  [t("track.destination"), pkg.destination],
                  [t("track.service"), pkg.service_type],
                  [t("track.weight"), `${pkg.weight_kg} kg`],
                  [t("track.eta"), pkg.estimated_delivery || "—"],
                  [t("track.current"), pkg.current_location],
                  ...(pkg.service_type === "Deposit" && pkg.depositor_name
                    ? [[t("track.depositor"), pkg.depositor_name]]
                    : []),
                  ...(pkg.service_type === "Deposit" && pkg.deposit_date
                    ? [[t("track.deposit_date"), new Date(pkg.deposit_date).toLocaleDateString(i18n.language || "en-GB")]]
                    : []),
                ].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/70 mb-1">{l}</div>
                    <div className="text-slate-100">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-2xl flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-amber-400" /> {t("track.corridor")}
                </h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400/70">
                  {t("track.live_route")}
                </span>
              </div>
              <RouteMap pkg={pkg} />
            </div>

            <div className="bg-[#0F1C3F]/60 border border-amber-500/20 rounded-2xl p-8">
              <h2 className="font-serif text-2xl mb-8 flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-400" /> {t("track.chain")}
              </h2>
              <ol className="relative border-l border-amber-500/25 ml-4 space-y-8">
                {[...pkg.checkpoints].reverse().map((c, i) => {
                  const isLatest = i === 0;
                  return (
                    <li key={`${c.timestamp}-${c.status}`} className="pl-8 relative" data-testid={`checkpoint-${i}`}>
                      <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${isLatest ? "bg-amber-400 border-amber-300 dvl-pulse" : "bg-[#0A1128] border-amber-500/50"}`} />
                      <div className="flex flex-wrap gap-3 items-center mb-1">
                        <span className={`border font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${statusTone(c.status)}`}>{c.status}</span>
                        <span className="text-xs text-slate-500 font-mono">{fmt(c.timestamp, i18n.language)}</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-100">
                        <MapPin className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
                        <div>
                          <div className="font-medium">{c.location}</div>
                          {c.notes && <div className="text-sm text-slate-400 mt-1">{c.notes}</div>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}

        {!pkg && !notFound && !loading && (
          <div className="text-center py-24">
            <Package className="w-10 h-10 text-amber-400/60 mx-auto mb-4" />
            <p className="text-slate-400">{t("track.empty")}</p>
          </div>
        )}
      </section>
    </>
  );
}
